import { Injectable } from '@angular/core';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { IClaims } from '@lsq-frontend/common-ui';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    DashboardClaim,
    Guid,
    VoidResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    CreatePersonDetailForClientRequest,
    CreatePersonDetailForClientService,
    CreatePersonResponse,
    GetPersonDetailForClientRequest,
    GetPersonDetailForClientService,
    GetPersonDetailForCurrentUserRequest,
    GetPersonDetailForCurrentUserService,
    GetUserClaimsForCurrentUserRequest,
    GetUserClaimsForCurrentUserService,
    GetUserClaimsForDashboardService,
    GetUserClaimsRequest,
    InviteUserRequest,
    InviteUserService,
    PersonDetail,
    ResendUserInviteRequest,
    ResendUserInviteService,
    UpdateUserClaimsForDashboardRequest,
    UpdateUserClaimsForDashboardService,
    UpdateUserStatusForClientService,
    UpdateUserStatusRequest
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { ClaimService, FeatureFlagService } from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { take } from 'rxjs/operators';
import { DashboardExperience } from '../../constants/experience.constants';
import {
    AddContactState,
    AdminClaimState,
    IPermissionsUiControl,
    ManageContactState
} from '../settings/settings.models';
import { SupplyChainDirectBuyerAccountSharedService } from '../supply-chain/direct/buyer-account/supply-chain-direct-buyer-account-shared.service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';

@Injectable({ providedIn: 'root' })
export class ManageUsersService {
    private _activeManageUserState = new ManageContactState();
    private _addUserState = new AddContactState();
    private _adminClaimState = new AdminClaimState();
    private _isFinancingSellerUser = false;
    private _isFinancingBuyerUser = false;
    private _isFinancingAccountUser = false;
    private _isFactoringUser = false;
    private _isAdminDocumentUser = false;
    private _isSystemManagementUser = false;
    private _isFromAdminPage = false; // Represents if this is accessed via administrative page (client management).
    private _clientDashboardExperiences: DashboardExperience[];
    private _isLsqAdminCompany = false;

    //Common Claims
    public manageDocumentsHeader = this._i18n({
        value: 'Manage Documents',
        meaning: 'Manage Documents permission header'
    });
    public manageDocumentsDescription = this._i18n({
        value: 'View and submit requested documents.',
        meaning: 'Manage Documents permission description'
    });

    // Factoring Claims
    public uploadInvoicesHeader = this._i18n({
        value: 'Upload Invoices',
        meaning: 'Upload Invoices permission header'
    });
    public uploadInvoicesDescription = this._i18n({
        value: 'Upload unpaid invoices for funding.',
        meaning: 'Upload Invoices permission description'
    });

    public manageCustomersHeader = this._i18n({
        value: 'Manage Customers',
        meaning: 'Manage Customers permission header'
    });
    public manageCustomersDescription = this._i18n({
        value: 'Request credit lines for new and existing customers.',
        meaning: 'Manage Customers permission description'
    });

    public viewReportsHeader = this._i18n({
        value: 'View Reports',
        meaning: 'View Reports permission header'
    });
    public viewReportsDescription = this._i18n({
        value: 'View and download reporting on account activity.',
        meaning: 'View Reports permission description'
    });

    public requestFundsHeader = this._i18n({
        value: 'Request Funds',
        meaning: 'Request Funds permission header'
    });
    public requestFundsDescription = this._i18n({
        value: 'Manage the disbursement of funds.',
        meaning: 'Request Funds permission description'
    });

    public managerUsersHeader = this._i18n({
        value: 'Manage Users',
        meaning: 'Manage Users permission header'
    });
    public managerUsersDescription = this._i18n({
        value: 'Manage user access and permissions.',
        meaning: 'Manage Users permission description'
    });

    // Financing Claims
    public manageBankAccountsHeader = this._i18n({
        value: 'Manage Payment Methods',
        meaning: 'Manage Bank Accounts permission header'
    });
    public manageBankAccountsDescription = this._i18n({
        value: 'Add, remove, or set default payment methods.',
        meaning: 'Manage Bank Accounts permission description'
    });

    public viewHistorialFundingBatchesHeader = this._i18n({
        value: 'View History',
        meaning: 'View Historical Funding Batches permission header'
    });
    public viewHistoricalFundingBatchesDescription = this._i18n({
        value: 'See a history of all early payment requests.',
        meaning: 'View Historical Funding Batches permission description'
    });

    public submitFundingBatchesHeader = this._i18n({
        value: 'Early Payment',
        meaning: 'Submit Funding Batches permission header'
    });
    public submitFundingBatchesDescription = this._i18n({
        value: 'Request early payment on invoices.',
        meaning: 'Submit Funding Batches permission description'
    });

    public manageScfUsersHeader = this._i18n({
        value: 'Manage Users',
        meaning: 'Manage Users permission header'
    });
    public manageScfSellerUsersDescription = this._i18n({
        value: 'Add, edit, or remove Users and manage their permissions.',
        meaning: 'Manage Users permission description'
    });

    public manageScfBuyerHeader = this._i18n({
        value: 'Buyer Management',
        meaning: 'Manage Sellers buyers permission header'
    });
    public manageScfBuyerDescription = this._i18n({
        value: 'Enroll new Buyers.',
        meaning: 'Manage Sellers buyers permission description'
    });

    public manageScfBuyerUsersDescription = this._i18n({
        value: 'Can add, edit, or remove company users.',
        meaning: 'Manage Users permission description'
    });

    public buyerInvoiceManagementHeader = this._i18n({
        value: 'Invoice Management',
        meaning: 'Invoice Management permission header'
    });
    public buyerInvoiceManagementDescription = this._i18n({
        value: 'Add or revoke invoices for Suppliers to request Early Payment.',
        meaning: 'Invoice Management permission description'
    });

    constructor(
        private _userService: UserService,
        private _claimService: ClaimService,
        private _getPersonDetailForCurrentUserService: GetPersonDetailForCurrentUserService,
        private _getPersonDetailForClientService: GetPersonDetailForClientService,
        private _getUserClaimsForCurrentUserService: GetUserClaimsForCurrentUserService,
        private _getUserClaimsForDashboardService: GetUserClaimsForDashboardService,
        private _updateUserClaimsForDashboardService: UpdateUserClaimsForDashboardService,
        private _createPersonDetailForClientService: CreatePersonDetailForClientService,
        private _updateUserStatusForClientService: UpdateUserStatusForClientService,
        private _resendUserInviteService: ResendUserInviteService,
        private _inviteUserService: InviteUserService,
        private _buyerAccountSharedService: SupplyChainDirectBuyerAccountSharedService,
        private _featureFlagService: FeatureFlagService,
        protected _i18n: I18n
    ) {
        SingletonServiceGuard.register('ManageUsersService');
        this._claimService.isOn(DashboardClaim.FactoringDashboard).subscribe((isFactoringUser) => {
            this._isFactoringUser = isFactoringUser;
        });

        this._claimService
            .isOn(DashboardClaim.FinancingVendorPortal)
            .subscribe((isFinancingUser) => {
                this._isFinancingSellerUser = isFinancingUser;
            });

        this._claimService
            .isOn(DashboardClaim.FinancingBuyerPortal)
            .subscribe((isFinancingBuyerUser) => {
                this._isFinancingBuyerUser = isFinancingBuyerUser;
            });

        this._claimService
            .isOn(DashboardClaim.FinancingAccountPortal)
            .subscribe((isFinancingAccountUser) => {
                this._isFinancingAccountUser = isFinancingAccountUser;
            });

        this._claimService
            .isOn(DashboardClaim.AdminViewDocument)
            .subscribe((isAdminDocumentUser) => {
                this._isAdminDocumentUser = isAdminDocumentUser;
            });

        this._claimService
            .isOn(DashboardClaim.SystemManagement)
            .subscribe((isSystemManagementUser) => {
                this._isSystemManagementUser = isSystemManagementUser;
            });
    }

    public isAdminClaims(): boolean {
        return (
            this._claimService.isOnSync(DashboardClaim.SystemAdminClaimManagement) &&
            this._featureFlagService.isOnSync(FeatureFlags.AdminClaims)
        );
    }

    // UI config.
    public async createStandardPermissionsUiControls(): Promise<IPermissionsUiControl[]> {
        let permissions: IPermissionsUiControl[] = [];

        if (this._canUpdateFinancingSellerClaims()) {
            permissions = permissions.concat([
                {
                    label: this.submitFundingBatchesHeader,
                    description: this.submitFundingBatchesDescription,
                    claims: [
                        DashboardClaim.FinancingViewFundingBatches,
                        DashboardClaim.FinancingSubmitFundingBatch
                    ]
                }
            ]);
        }

        if (this._canUpdateFinancingBuyerClaims()) {
            if (await this._canBuyerRevokeInvoices()) {
                permissions = permissions.concat([
                    {
                        label: this.buyerInvoiceManagementHeader,
                        description: this.buyerInvoiceManagementDescription,
                        claims: [
                            DashboardClaim.FinancingBuyerPortalViewInvoices,
                            DashboardClaim.FinancingBuyerPortalEditInvoices
                        ]
                    }
                ]);
            }
        }

        if (this._canUpdateFinancingAccountClaims()) {
            if (await this._canBuyerRevokeInvoices()) {
                permissions = permissions.concat([
                    {
                        label: this.buyerInvoiceManagementHeader,
                        description: this.buyerInvoiceManagementDescription,
                        claims: [
                            DashboardClaim.FinancingAccountPortalViewInvoices,
                            DashboardClaim.FinancingAccountPortalEditInvoices
                        ]
                    }
                ]);
            }
        }

        if (this._canUpdateFactoringClaims()) {
            permissions = permissions.concat([
                {
                    label: this.uploadInvoicesHeader,
                    description: this.uploadInvoicesDescription,
                    claims: [DashboardClaim.ViewInvoiceUploads, DashboardClaim.SubmitInvoiceUploads]
                },
                {
                    label: this.manageCustomersHeader,
                    description: this.manageCustomersDescription,
                    claims: [DashboardClaim.ViewCustomers, DashboardClaim.SubmitCreditRequests]
                },
                {
                    label: this.viewReportsHeader,
                    description: this.viewReportsDescription,
                    claims: [DashboardClaim.ViewReports]
                }
            ]);
        }

        return permissions;
    }

    public createAdminPermissionsUiControls(): IPermissionsUiControl[] {
        let permissions: IPermissionsUiControl[] = [];

        if (this._canUpdateFinancingSellerClaims()) {
            permissions = permissions.concat([
                {
                    label: this.manageBankAccountsHeader,
                    description: this.manageBankAccountsDescription,
                    hasWarningIcon: true,
                    claims: [
                        DashboardClaim.FinancingViewBankAccounts,
                        DashboardClaim.FinancingEditBankAccounts
                    ]
                },
                {
                    label: this.manageScfUsersHeader,
                    description: this.manageScfSellerUsersDescription,
                    hasWarningIcon: true,
                    claims: [DashboardClaim.FinancingViewUsers, DashboardClaim.FinancingEditUsers]
                },
                {
                    label: this.manageScfBuyerHeader,
                    description: this.manageScfBuyerDescription,
                    hasWarningIcon: true,
                    claims: [DashboardClaim.FinancingSellerEnrollRelationship]
                }
            ]);
        }

        if (this._canUpdateFinancingBuyerClaims()) {
            permissions = permissions.concat([
                {
                    label: this.manageScfUsersHeader,
                    description: this.manageScfBuyerUsersDescription,
                    hasWarningIcon: true,
                    claims: [
                        DashboardClaim.FinancingBuyerPortalViewUsers,
                        DashboardClaim.FinancingBuyerPortalEditUsers
                    ]
                }
            ]);
        }

        if (this._canUpdateFinancingAccountClaims()) {
            permissions = permissions.concat([
                {
                    label: this.manageScfUsersHeader,
                    description: this.manageScfBuyerUsersDescription,
                    hasWarningIcon: true,
                    claims: [
                        DashboardClaim.FinancingAccountPortalViewUsers,
                        DashboardClaim.FinancingAccountPortalEditUsers
                    ]
                }
            ]);
        }

        if (this._canUpdateFactoringClaims()) {
            permissions = permissions.concat([
                {
                    label: this.requestFundsHeader,
                    description: this.requestFundsDescription,
                    hasWarningIcon: true,
                    claims: [DashboardClaim.ViewRequestFunds, DashboardClaim.SubmitRequestFunds]
                },
                {
                    label: this.managerUsersHeader,
                    description: this.managerUsersDescription,
                    hasWarningIcon: true,
                    claims: [DashboardClaim.ViewUsers, DashboardClaim.EditUsers]
                }
            ]);
        }

        if (this._canUpdateDocumentManagementClaims()) {
            permissions = permissions.concat([
                {
                    label: this.manageDocumentsHeader,
                    description: this.manageDocumentsDescription,
                    hasWarningIcon: true,
                    claims: [DashboardClaim.ViewDocuments, DashboardClaim.EditDocuments]
                }
            ]);
        }

        if (this._isLsqAdminCompany) {
            permissions = permissions.concat([
                {
                    label: 'Client Management',
                    description: '',
                    hasWarningIcon: false,
                    claims: [DashboardClaim.SystemManagement],
                    isOn: true,
                    isDisabled: true
                }
            ]);
        }

        return permissions;
    }

    public async manageUser(
        personKey: Guid,
        companyKey: Guid,
        isFromAdminPage?: boolean,
        clientDashboardExperiences?: DashboardExperience[],
        isLsqAdminCompany = false
    ): Promise<ManageContactState> {
        this._isLsqAdminCompany = isLsqAdminCompany;
        this._isFromAdminPage = isFromAdminPage;
        this._clientDashboardExperiences = clientDashboardExperiences;

        const getPersonDetailForClientRequest = new GetPersonDetailForClientRequest({
            companyKey: companyKey,
            personKey: personKey
        });
        const { personDetail } = await this._getPersonDetailForClientService.executeOnce(
            getPersonDetailForClientRequest
        );

        // Get claims for that user.
        const getUserClaimsForDashboardRequest = new GetUserClaimsRequest({
            companyKey: companyKey,
            userKey: personDetail.userKey
        });
        const { claims } = await this._getUserClaimsForDashboardService.executeOnce(
            getUserClaimsForDashboardRequest
        );

        await this._setPersonDetail(personDetail, claims);

        return this._activeManageUserState;
    }

    public async manageMyProfile(): Promise<ManageContactState> {
        // Get current user's profile.
        const getPersonDetailForCurrentUserRequest = new GetPersonDetailForCurrentUserRequest({
            companyKey: this._userService.getCompanyKey()
        });
        const { personDetail } = await this._getPersonDetailForCurrentUserService.executeOnce(
            getPersonDetailForCurrentUserRequest
        );

        // Get current user's claims.
        const getUserClaimsForCurrentUserRequest = new GetUserClaimsForCurrentUserRequest({
            companyKey: this._userService.getCompanyKey()
        });
        const { claims } = await this._getUserClaimsForCurrentUserService.executeOnce(
            getUserClaimsForCurrentUserRequest
        );

        await this._setPersonDetail(personDetail, claims);

        return this._activeManageUserState;
    }

    // Add User flow.
    public async addUser(
        companyKey?: Guid,
        isFromAdminPage?: boolean,
        clientDashboardExperiences?: DashboardExperience[],
        isLsqAdminCompany = false
    ): Promise<AddContactState> {
        this._isFromAdminPage = isFromAdminPage;
        this._clientDashboardExperiences = clientDashboardExperiences;
        this._isLsqAdminCompany = isLsqAdminCompany;

        this._addUserState = new AddContactState();
        this._addUserState.adminPermissionsUiControls = this.createAdminPermissionsUiControls();
        this._addUserState.standardPermissionsUiControls = await this.createStandardPermissionsUiControls();

        if (companyKey) {
            this._addUserState.companyKey = companyKey;
        }

        return this._addUserState;
    }

    public updateAddUserFormValue(formValue?: any): void {
        if (!!this._addUserState.formValue) {
            this._addUserState.formValue = formValue;
        } else {
            this._addUserState.formValue = {
                ...this._addUserState.formValue,
                ...formValue
            };
        }
    }

    public async createUserForClient(): Promise<CreatePersonResponse> {
        const formValue = this._addUserState.formValue;

        // Generate the list of claims.
        const claimsMap = {
            ...this.getClaimsFromUiControls({
                admin: this._addUserState.adminPermissionsUiControls,
                standard: this._addUserState.standardPermissionsUiControls
            }),
            ...this.getAdminClaimsFromUIControls()
        };

        const claims = Object.keys(claimsMap)
            .filter((claimNumberAsStr) => claimsMap[claimNumberAsStr])
            .map((claimNumberAsStr) => parseInt(claimNumberAsStr, 10));

        if (this._canUpdateFinancingBuyerClaims()) {
            claims.push(DashboardClaim.FinancingBuyerPortal);
        } else if (this._canUpdateFinancingSellerClaims()) {
            claims.push(DashboardClaim.FinancingVendorPortal);
        } else if (this._canUpdateFinancingAccountClaims()) {
            claims.push(DashboardClaim.FinancingAccountPortal);
            claims.push(DashboardClaim.FinancingAccountPortalViewAllScfData);
        }

        // Create the contact.
        const createPersonForClientRequest = new CreatePersonDetailForClientRequest({
            companyKey: this._addUserState.companyKey
                ? this._addUserState.companyKey
                : this._userService.getCompanyKey(),
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            title: formValue.title,
            emailAddress: formValue.emailAddress,
            phoneNumber: formValue.phoneNumber,
            phoneNumberExtension: formValue.extension
        });

        const createPeopleDetailForClientResponse = await this._createPersonDetailForClientService.executeOnce(
            createPersonForClientRequest
        );

        const inviteUserRequest = new InviteUserRequest({
            companyKey: this._addUserState.companyKey
                ? this._addUserState.companyKey
                : this._userService.getCompanyKey(),
            personKey: createPeopleDetailForClientResponse.personKey,
            claimsToAdd: claims
        });

        await this._inviteUserService.executeOnce(inviteUserRequest);

        return createPeopleDetailForClientResponse;
    }

    public async createUserForProspect(): Promise<CreatePersonResponse> {
        const formValue = this._addUserState.formValue;

        // Generate the list of claims.
        const claimsMap = {
            ...this.getClaimsFromUiControls({
                admin: this._addUserState.adminPermissionsUiControls,
                standard: this._addUserState.standardPermissionsUiControls
            }),
            ...this.getAdminClaimsFromUIControls()
        };

        const claims = Object.keys(claimsMap)
            .filter((claimNumberAsStr) => claimsMap[claimNumberAsStr])
            .map((claimNumberAsStr) => parseInt(claimNumberAsStr, 10));

        // Create the contact.
        const createPersonForClientRequest = new CreatePersonDetailForClientRequest({
            companyKey: this._addUserState.companyKey
                ? this._addUserState.companyKey
                : this._userService.getCompanyKey(),
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            title: formValue.title,
            emailAddress: formValue.emailAddress,
            phoneNumber: formValue.phoneNumber,
            phoneNumberExtension: formValue.extension
        });

        const createPeopleDetailForClientResponse = await this._createPersonDetailForClientService.executeOnce(
            createPersonForClientRequest
        );

        const inviteUserRequest = new InviteUserRequest({
            companyKey: this._addUserState.companyKey
                ? this._addUserState.companyKey
                : this._userService.getCompanyKey(),
            personKey: createPeopleDetailForClientResponse.personKey,
            claimsToAdd: claims
        });

        await this._inviteUserService.executeOnce(inviteUserRequest);
        return createPeopleDetailForClientResponse;
    }

    // Getters.
    public get activeManageUserState(): ManageContactState {
        return this._activeManageUserState;
    }

    public get addUserState(): AddContactState {
        return this._addUserState;
    }

    public get adminClaimState(): AdminClaimState {
        return this._adminClaimState;
    }

    public set setAdminClaims(adminClaimState: AdminClaimState) {
        this._adminClaimState = adminClaimState;
    }

    public getClaimsFromUiControls(controls: {
        admin: IPermissionsUiControl[];
        standard: IPermissionsUiControl[];
    }): IClaims {
        return [...controls.admin, ...controls.standard].reduce<IClaims>(
            (claims, currentElement) => {
                currentElement.claims.forEach((claim) => {
                    claims[claim] = !!currentElement.isOn;
                });

                return claims;
            },
            {}
        );
    }

    private getAdminClaimsFromUIControls(): IClaims {
        const adminSystemManagementUiControl = this._adminClaimState.adminSystemManagementUiControl;
        if (adminSystemManagementUiControl && adminSystemManagementUiControl.isOn) {
            const adminClaimManagementUiControls = this._adminClaimState
                .adminClaimManagementUiControls;

            const adminClaims = adminClaimManagementUiControls.reduce<IClaims>(
                (claims, currentElement) => {
                    currentElement.viewClaims.forEach((claim) => {
                        claims[claim] = !!currentElement.viewIsOn;
                    });

                    currentElement.editClaims.forEach((claim) => {
                        claims[claim] = !!currentElement.editIsOn;
                    });

                    return claims;
                },
                {}
            );

            return {
                ...adminClaims,
                ...this.getClaimsOn(adminSystemManagementUiControl),
                ...this.getClaimsOn(this._adminClaimState.adminSystemClaimManagementUiControl)
            };
        } else {
            return {
                ...this.getClaimsOn(this._adminClaimState.adminSystemClaimManagementUiControl)
            };
        }
    }

    private getClaimsOn(control: IPermissionsUiControl): IClaims {
        if (control) {
            return [control].reduce<IClaims>((claims, currentElement) => {
                currentElement.claims.forEach((claim) => {
                    claims[claim] = !!currentElement.isOn;
                });

                return claims;
            }, {});
        }
        return {};
    }

    public updateClaims(companyKey?: Guid): Promise<VoidResponse> {
        const userKey = this._activeManageUserState.personDetail.userKey;
        const claims = {
            ...this.getClaimsFromUiControls({
                admin: this._activeManageUserState.adminPermissionsUiControls,
                standard: this._activeManageUserState.standardPermissionsUiControls
            }),
            ...this.getAdminClaimsFromUIControls()
        };
        this._activeManageUserState.companyKey = companyKey;

        const claimsArr = Object.keys(claims)
            .filter((claimNumberAsStr) => claims[claimNumberAsStr])
            .map((claimNumberAsStr) => parseInt(claimNumberAsStr, 10));

        const updateUserClaimsForDashboardRequest = new UpdateUserClaimsForDashboardRequest({
            userKey: userKey,
            companyKey: companyKey || this._userService.getCompanyKey(),
            claims: claimsArr
        });

        return this._updateUserClaimsForDashboardService.executeOnce(
            updateUserClaimsForDashboardRequest
        );
    }

    public async setUserActiveState(
        personDetail: PersonDetail,
        companyKey: Guid,
        isActive: boolean
    ): Promise<void> {
        const updateUserStatusRequest: UpdateUserStatusRequest = new UpdateUserStatusRequest({
            userKey: personDetail.userKey,
            companyKey: companyKey,
            active: isActive
        });
        await this._updateUserStatusForClientService.executeOnce(updateUserStatusRequest);
    }

    public async reinviteUser(userKey: string): Promise<void> {
        await this._resendUserInviteService.executeOnce(
            new ResendUserInviteRequest({
                userKey
            })
        );
    }

    private async _setPersonDetail(
        personDetail: PersonDetail,
        claims: DashboardClaim[]
    ): Promise<void> {
        // Set `person` and `personKey` on `this._activeManageUserState` to `null`
        // to indicate that we're loading up a new user.
        this._activeManageUserState = new ManageContactState();
        this._activeManageUserState.adminPermissionsUiControls = this.createAdminPermissionsUiControls().map(
            setClaimSwitch
        );
        this._activeManageUserState.standardPermissionsUiControls = (
            await this.createStandardPermissionsUiControls()
        ).map(setClaimSwitch);
        this._activeManageUserState.claims = claims;
        this._activeManageUserState.personDetail = personDetail;
        this._activeManageUserState.personKey = personDetail.personKey;

        // Set each UI control to reflect the user's claims.
        function setClaimSwitch(permissionControl: IPermissionsUiControl): IPermissionsUiControl {
            return {
                ...permissionControl,
                isOn: permissionControl.claims.every((claim) => claims.indexOf(claim) >= 0)
            };
        }
    }

    private _isSystemManagementUserUpdatingPermissions(): boolean {
        return this._isSystemManagementUser && this._isFromAdminPage;
    }

    private _canUpdateFinancingSellerClaims(): boolean {
        const isSystemManagementUpdating = this._isSystemManagementUserUpdatingPermissions();
        return (
            (!isSystemManagementUpdating && this._isFinancingSellerUser) ||
            (isSystemManagementUpdating &&
                this._clientDashboardExperiences.includes(DashboardExperience.SCF_Seller))
        );
    }

    private _canUpdateFinancingBuyerClaims(): boolean {
        const isSystemManagementUpdating = this._isSystemManagementUserUpdatingPermissions();
        return (
            (!isSystemManagementUpdating && this._isFinancingBuyerUser) ||
            (isSystemManagementUpdating &&
                this._clientDashboardExperiences.includes(DashboardExperience.SCF_Buyer))
        );
    }

    private async _canBuyerRevokeInvoices(): Promise<boolean> {
        const isSystemManagementUpdating = this._isSystemManagementUserUpdatingPermissions();
        if (isSystemManagementUpdating) {
            // We won't be able to look up the Buyer/Account ConfigurationSummary if you are
            // an admin user who searched for a company on the Client Mgmt page.
            // Assuming the buyer can revoke invoices for this use case will have no adverse effects.
            return true;
        }

        const canRevokeInvoices = await this._buyerAccountSharedService.canRevokeInvoices$
            .pipe(take(1))
            .toPromise();
        return canRevokeInvoices;
    }

    private _canUpdateFinancingAccountClaims(): boolean {
        const isSystemManagementUpdating = this._isSystemManagementUserUpdatingPermissions();
        return (
            (!isSystemManagementUpdating && this._isFinancingAccountUser) ||
            (isSystemManagementUpdating &&
                this._clientDashboardExperiences.includes(DashboardExperience.SCF_Account))
        );
    }

    private _canUpdateFactoringClaims(): boolean {
        const isSystemManagementUpdating = this._isSystemManagementUserUpdatingPermissions();
        return (
            (!isSystemManagementUpdating && this._isFactoringUser) ||
            (isSystemManagementUpdating &&
                this._clientDashboardExperiences.includes(DashboardExperience.Factoring))
        );
    }

    private _canUpdateDocumentManagementClaims(): boolean {
        return (
            this._featureFlagService.isOnSync(FeatureFlags.DocumentCenter) &&
            this._isAdminDocumentUser
        );
    }
}
